import React, { useState, useEffect } from 'react';
import { TextField, Checkbox, Button } from '@cimpress/react-components';
import { Template } from '../models/truck';
import { useSelector, useDispatch } from 'react-redux';
import { updateTruckTemplate, createTruckTemplate, deleteTruckTemplate } from '../store/actions/truck-template';
import { ValueType } from 'react-select';
import { isUndefined } from 'util';
import { Option, Options } from '../models/utils';
import { valueToArray } from '../utils/functions';
import { Select } from './Select';
import { FlexBox, FlexGrow } from '../utils/css-classes';
import { StoreState } from '../store/state';

export interface TemplateFormProps {
  close: () => void;
  template?: Template;
}

const loadingRestrictionNames: { [k: string]: string; } = {
  'ShipmentQualifierTruckLoadingRestriction::tariff-category': 'shipment restrictions',
  'ShipmentQualifierTruckLoadingRestriction::carrier-account': 'carrier restrictions',
};

// tslint:disable-next-line:function-name
export default function TemplateForm({ template, close }: TemplateFormProps) {
  const dispatch = useDispatch();

  const loadingRestrictions = useSelector<StoreState, Record<string, Options>>(
    state => state.loadingRestrictions.all
      ? Object.entries(state.loadingRestrictions.all)
          .reduce(
            (acc, [name, restrictions]) => ({
              ...acc,
              [name]: Object.values(restrictions).map(r => ({ label: r.displayValue, value: r.value })),
            }),
            {} as Record<string, Options>,
          )
      : {},
  );
  const linehaulCompanies = useSelector<StoreState, Options>(
    state => Object.values(state.linehaulCompanies.all).map(c => ({ label: c.name, value: c.id })),
  );
  const customsBrokers = useSelector<StoreState, Options>(
    state => Object.values(state.customsBrokers.all).map(b => ({ label: b.name, value: b.id })),
  );

  const [name, setName] = useState('');
  const [truckName, setTruckName] = useState('');
  const [truckLinehaulCompany, setTruckLinehaulCompany] = useState<ValueType<Option>>();
  const [truckLoadingRestrictions, setTruckLoadingRestrictions] = useState<Record<string, Options>>({});
  const [crossesBorder, setCrossesBorder] = useState(false);
  const [customsBroker, setCustomsBroker] = useState<ValueType<Option>>();

  const deleteTemplate = () => {
    if (template) {
      dispatch(deleteTruckTemplate.request(template));
    }
    close();
  };

  const submit = () => {
    const loadingRestrictions = [] as { restrictionType: string, value: string; }[];
    for (const [restrictionType, values] of Object.entries(truckLoadingRestrictions)) {
      loadingRestrictions.push(
        ...values.map(o => ({ restrictionType, value: o.value })),
      );
    }
    const linehaulCompanyId = truckLinehaulCompany ? valueToArray(truckLinehaulCompany)[0].value : null;
    const customsBrokerId = customsBroker ? valueToArray(customsBroker)[0].value : null;

    if (template) {
      dispatch(updateTruckTemplate.request({
        name,
        truckName,
        crossesBorder,
        linehaulCompanyId,
        loadingRestrictions,
        customsBrokerId,
        id: template.id,
      }));
    } else {
      dispatch(createTruckTemplate.request({
        name,
        truckName,
        crossesBorder,
        linehaulCompanyId,
        customsBrokerId,
        loadingRestrictions,
      }));
    }
    close();
  };

  useEffect(
    () => {
      if (template) {
        const truckLoadingRestrictions = {} as Record<string, Options>;
        for (const { restrictionType, value } of template.loadingRestrictions) {
          if (isUndefined(truckLoadingRestrictions[restrictionType])) {
            truckLoadingRestrictions[restrictionType] = [];
          }

          const restriction = loadingRestrictions[restrictionType].find(r => r.value === value);
          if (restriction) {
            truckLoadingRestrictions[restrictionType].push(restriction);
          }
        }
        setName(template.name);
        setTruckName(template.truckName || '');
        setTruckLoadingRestrictions(truckLoadingRestrictions);
        setTruckLinehaulCompany(linehaulCompanies.find(company => company.value === template.linehaulCompanyId));
        setCrossesBorder(template.crossesBorder);
        setCustomsBroker(customsBrokers.find(broker => broker.value === template.customsBrokerId));
      }
    },
    [template],
  );

  return <>
    <TextField
      required
      label="Template Name"
      value={name}
      onChange={e => setName(e.target.value)}
    />
    <TextField
      required
      label="Default Truck Name"
      value={truckName}
      onChange={e => setTruckName(e.target.value)}
    />
    {
      linehaulCompanies.length > 0
      && <Select
        tether
        isClearable
        label="Select a line haul company"
        value={truckLinehaulCompany}
        options={linehaulCompanies}
        onChange={e => setTruckLinehaulCompany(e)}
      />
    }
    {
      Object.keys(loadingRestrictions).map(
        type =>
          <Select
            tether
            isMulti
            isClearable
            key={type}
            label={`Select ${loadingRestrictionNames[type]}`}
            labelKey="displayValue"
            value={truckLoadingRestrictions[type]}
            options={Object.values(loadingRestrictions[type])}
            onChange={e => setTruckLoadingRestrictions({
              ...truckLoadingRestrictions,
              [type]: valueToArray(e),
            })}
          />,
      )
    }
    <Checkbox
      label="Truck will be crossing an international border"
      checked={crossesBorder}
      onChange={() => setCrossesBorder(!crossesBorder)}
    />
    {
      crossesBorder && customsBrokers.length > 0
      && <Select
        tether
        isClearable
        label="Select a customs broker"
        value={customsBroker}
        options={customsBrokers}
        onChange={e => setCustomsBroker(e)}
      />
    }
    <div className={FlexBox.FLEX}>
      {
        template
        && <Button
          style={{
            backgroundColor: 'var(--danger)',
            borderColor: 'var(--danger)',
            color: 'white',
          }}
          onClick={deleteTemplate}
        >
          <i className="fa fa-trash" /> Delete Template
        </Button>
      }
      <span className={FlexGrow.GROW_1}/>
      <Button onClick={close} style={{ marginRight: '5px' }}>
        Cancel
      </Button>
      <Button type="primary" onClick={submit}>
        Save
      </Button>
    </div>
  </>;
}
